import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import PageFaq from "../../components/content/PageFaq";
import ConversionCtas from "../../components/content/ConversionCtas";
import { buildProductJsonLd, buildFaqPageJsonLd } from "../../lib/seoJsonLd";
import { PRODUCTS } from "../../lib/data";
import { BLENDS } from "../../lib/blends";
import { getIngredientById } from "../../lib/ingredients";
import { PHOTO_BRIEF } from "../../lib/contentImagePlaceholders";

const heroImg = PRODUCTS.find((p) => p.id === "custom-500")?.image || "";

const customFaqs = [
  {
    question: "How does the custom blender work?",
    answer:
      "Pick your sea moss base, then choose a format. The 250 ml foil pouch and 300 ml glass jar are plain sea moss only. Custom powdered botanicals (up to three) are available on 500 ml and 1 litre glass jars only. Press blend, watch the animation, then add to basket.",
  },
  {
    question: "Are named blends medical formulas?",
    answer:
      "No. Names like “Daily Glow” are tasting notes and inspiration only. They do not treat, cure, or prevent any condition.",
  },
  {
    question: "Why don’t you blend fresh fruit into the gel?",
    answer:
      "Fresh fruit can shorten shelf life, ferment inside gel, or stress packaging in transit. MossMatrix shipped gel uses powdered botanicals only for custom jars. Fruit can be mixed in at home, but our shipped custom blends are herb-based for freshness and consistency.",
  },
  {
    question: "Can I recreate a blend later?",
    answer:
      "Yes. After blending you can copy a share link from the builder so you (or a friend) can reopen the same configuration.",
  },
];

export default function CustomSeaMossBlendPage() {
  const path = "/products/custom-sea-moss-blend";
  const productLd = buildProductJsonLd({
    name: "Custom Sea Moss Blend",
    description:
      "MossMatrix custom sea moss gel: choose base, size, up to three powdered botanicals on 500 ml or 1 L jars, press blend, add to basket. Interactive jar reveal and checkout-ready cart.",
    urlPath: path,
    image: heroImg,
    lowPrice: "14.99",
    highPrice: "24.99",
    offerCount: "4",
  });

  return (
    <SiteShell>
      <SEO
        title="Custom Sea Moss Blend | Base, jar size and powdered botanicals"
        description="Build MossMatrix custom sea moss gel online: gold or purple base, jar size, up to three powdered botanicals on 500 ml or 1 L, blend animation, jar reveal. Popular blend ideas with honest, non-medical copy."
        path={path}
        ogType="product"
        jsonLd={[productLd, buildFaqPageJsonLd(customFaqs)]}
      />
      <ContentPageLayout
        eyebrow="Experience"
        title="Custom Sea Moss Blend"
        subtitle="Choose your base, choose your size, add up to three powdered botanicals on 500 ml or 1 L jars, press blend, then add to basket. Same premium gel as our ready-made jars, composed in your browser."
      >
        <div className="grid lg:grid-cols-2 gap-10 items-start mb-14">
          <div>
            <div className="rounded-[2rem] overflow-hidden border border-cream aspect-[4/5] max-h-[480px]">
              <img src={heroImg} alt="Custom MossMatrix sea moss blend" className="w-full h-full object-cover editorial-img" />
            </div>
            <p className="mt-2 text-[10px] text-botanical/50 leading-relaxed">{PHOTO_BRIEF.spoonTexture}</p>
          </div>
          <ol className="space-y-4 list-decimal list-inside text-sm text-botanical/85 leading-relaxed rounded-2xl border border-cream bg-white p-6">
            <li className="pl-1">
              <span className="font-medium text-forest">Choose base</span>: Gold or Purple sea moss.
            </li>
            <li className="pl-1">
              <span className="font-medium text-forest">Choose format and size</span>: Foil pouch 250 ml is plain only. Glass jar: 300 ml plain only; 500 ml and 1 litre can be plain or custom blend (powdered botanicals only).
            </li>
            <li className="pl-1">
              <span className="font-medium text-forest">Add up to three powdered botanicals</span>: drag into the blender or tap cards on mobile.
            </li>
            <li className="pl-1">
              <span className="font-medium text-forest">Press blend</span>: watch the choreographed blend, then your finished jar.
            </li>
            <li className="pl-1">
              <span className="font-medium text-forest">Add to basket</span>: checkout when you are ready.
            </li>
          </ol>
        </div>

        <section className="rounded-3xl border border-cream bg-offwhite p-8 md:p-10 mb-10">
          <h2 className="font-serif text-2xl text-forest">Popular blend routines</h2>
          <p className="mt-2 text-sm text-botanical/75 max-w-2xl">
            Starting points for herbal inspiration, not prescriptions. We keep shipped custom gel to powdered botanicals for freshness and predictable UK delivery. Add finished gel to shakes, oats, yoghurt, or drinks at home if you like.{" "}
            <Link to="/blends" className="underline text-forest hover:text-gold">
              View all routines
            </Link>
            .
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {BLENDS.map((b) => {
              const line = b.ingredientIds.map((id) => getIngredientById(id)?.name || id).join(" + ");
              return (
                <Link
                  key={b.slug}
                  to={`/blends/${b.slug}`}
                  className="group rounded-2xl border border-cream bg-white p-5 hover:border-gold/50 hover:shadow-soft transition block"
                >
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gold">{b.name}</div>
                  <div className="font-serif text-lg text-forest mt-2 group-hover:text-botanical transition">{line}</div>
                  <p className="mt-2 text-xs text-botanical/80 leading-relaxed line-clamp-3">{b.routineCopy}</p>
                  <span className="mt-3 inline-block text-[10px] tracking-widest uppercase text-forest/50 group-hover:text-forest">
                    Read routine →
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        <div className="rounded-2xl border border-cream bg-white px-6 py-5 text-center mb-10">
          <Link
            to="/#builder"
            className="inline-flex rounded-full bg-gold text-forest px-8 py-3.5 text-sm font-medium hover:bg-goldhover transition"
          >
            Open the blender
          </Link>
          <p className="mt-3 text-xs text-botanical/60">{PHOTO_BRIEF.customerHolding}</p>
        </div>

        <PageFaq items={customFaqs} />
        <ConversionCtas />

        <p className="text-center text-sm text-botanical/70 mt-10">
          <Link to="/shop" className="underline hover:text-forest">
            Back to shop
          </Link>
          {" · "}
          <Link to="/ingredients" className="underline hover:text-forest">
            Ingredient library
          </Link>
          {" · "}
          <Link to="/learn/what-is-sea-moss" className="underline hover:text-forest">
            What is sea moss?
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-forest">
            FAQ
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
